import React, { useState, useMemo, useEffect } from 'react';
import { Copy } from 'lucide-react';
import { toast } from 'react-toastify';
import { seoAPI } from '../../../config/seo-api';
import { helpers } from '../../../utils/helpers';
import {
  buildSeoPreviewUrl,
  buildSeoSavePayload,
  calculateSeoScore,
  getPrimaryProductCategory,
  getProductUrlCategorySegment,
  getProductImageUrl,
  getSeoStatusClass,
  getSeoStatusFromScore,
  initializeSeoFormData,
  slugifyCategory,
} from '../../../utils/seoHelpers';

const META_TITLE_MAX = 70;
const META_DESCRIPTION_MAX = 160;

const EditSeoModal = ({ product, adminData, onClose, onSaved }) => {
  const canEdit = helpers.hasSeoEditAccess(adminData);

  const [activeTab, setActiveTab] = useState('basic');
  const [productData, setProductData] = useState(null);
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        setLoadError(null);

        const response = await seoAPI.fetchSeoProduct(product.productId, product._id);

        if (response.success) {
          const data = response.data;
          setProductData(data);
          setFormData(initializeSeoFormData(data));
        } else {
          setLoadError(response.msg || response.error || 'Failed to load product SEO data');
        }
      } catch (error) {
        console.error('SEO product fetch error:', error);
        setLoadError('Network error. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [product.productId, product._id]);

  const previewUrl = useMemo(() => {
    if (!productData || !formData) return '';
    return buildSeoPreviewUrl(productData, formData.urlSlug, formData.categorySlug);
  }, [productData, formData]);

  const liveScore = useMemo(() => {
    if (!formData) return 0;
    return calculateSeoScore({
      seoData: {
        productTitle: formData.productTitle,
        metaTitle: formData.metaTitle,
        metaDescription: formData.metaDescription,
        urlSlug: formData.urlSlug,
        categorySlug: formData.categorySlug,
        productDescription: formData.productDescription,
        productSchemaMarkup: formData.productSchemaMarkup,
        breadCrumbSchema: formData.breadCrumbSchema,
        faqSchema: formData.faqSchema,
      },
      images: formData.images,
    });
  }, [formData]);

  const categoryPathPreview = useMemo(() => {
    if (!productData || !formData) return slugifyCategory('category');
    return getProductUrlCategorySegment(productData, formData.categorySlug);
  }, [productData, formData]);

  const statusForDisplay = getSeoStatusFromScore(liveScore);
  const statusClass = getSeoStatusClass(statusForDisplay);

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAltTextChange = (index, value) => {
    setFormData((prev) => {
      const images = [...prev.images];
      images[index] = { ...images[index], altText: value };
      return { ...prev, images };
    });
  };

  const handleSchemaChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!canEdit || !productData || !formData) return;

    try {
      setSaving(true);
      const payload = buildSeoSavePayload(productData, formData);
      const response = await seoAPI.saveSeo(payload);

      if (response.success) {
        toast.success(response.msg || 'SEO saved successfully');
        onSaved?.(response.data);
        onClose();
      } else {
        toast.error(response.msg || response.error || 'Failed to save SEO');
      }
    } catch (error) {
      console.error('SEO save error:', error);
      toast.error('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyUrl = async () => {
    if (!previewUrl) return;

    try {
      await navigator.clipboard.writeText(previewUrl);
      toast.success('Product URL copied to clipboard');
    } catch (error) {
      console.error('Copy URL error:', error);
      toast.error('Failed to copy URL');
    }
  };

  const productName = productData?.name || product?.name || 'Product';

  return (
    <div className="seo-modal-overlay" onClick={onClose}>
      <div className="seo-modal" onClick={(e) => e.stopPropagation()}>
        <div className="seo-modal-header">
          <div className="seo-modal-header-left">
            <h3>Edit SEO — {productName}</h3>
            {!loading && !loadError && formData && (
              <div className="seo-modal-score-preview">
                <span className={`seo-status-badge ${statusClass}`}>{statusForDisplay}</span>
                <span className="seo-modal-score-text">{liveScore}/100</span>
              </div>
            )}
          </div>
          <div className="seo-modal-header-actions">
            {!loading && !loadError && previewUrl && (
              <button
                type="button"
                className="seo-modal-copy"
                onClick={handleCopyUrl}
                title="Copy product URL"
              >
                <Copy size={18} />
              </button>
            )}
            <button type="button" className="seo-modal-close" onClick={onClose}>
              ×
            </button>
          </div>
        </div>

        {loading ? (
          <div className="seo-modal-loading">
            <div className="seo-loading-spinner" />
            <p>Loading SEO data...</p>
          </div>
        ) : loadError ? (
          <div className="seo-modal-error">
            <p>{loadError}</p>
            <button type="button" className="seo-retry-button" onClick={onClose}>
              Close
            </button>
          </div>
        ) : (
          <>
            <div className="seo-modal-tabs">
              <button
                type="button"
                className={`seo-tab ${activeTab === 'basic' ? 'seo-tab-active' : ''}`}
                onClick={() => setActiveTab('basic')}
              >
                Basic SEO
              </button>
              <button
                type="button"
                className={`seo-tab ${activeTab === 'advanced' ? 'seo-tab-active' : ''}`}
                onClick={() => setActiveTab('advanced')}
              >
                Advanced SEO
              </button>
            </div>

            <div className="seo-modal-content">
              {activeTab === 'basic' ? (
                <div className="seo-basic-grid">
                  <div className="seo-basic-left">
                    <div className="seo-form-group">
                      <label>Product Title</label>
                      <input
                        type="text"
                        className="seo-form-input"
                        value={formData.productTitle}
                        onChange={(e) => handleFieldChange('productTitle', e.target.value)}
                        disabled={!canEdit}
                      />
                      <span className="seo-field-hint">This is the name that appears on your product page.</span>
                    </div>

                    <div className="seo-form-group">
                      <label>Meta Title</label>
                      <input
                        type="text"
                        className="seo-form-input"
                        value={formData.metaTitle}
                        maxLength={META_TITLE_MAX}
                        onChange={(e) => handleFieldChange('metaTitle', e.target.value)}
                        disabled={!canEdit}
                      />
                      <div className="seo-char-counter">
                        {formData.metaTitle.length}/{META_TITLE_MAX} characters
                      </div>
                      <span className="seo-field-hint">Title tag that appears in search engine results.</span>
                    </div>

                    <div className="seo-form-group">
                      <label>Meta Description</label>
                      <textarea
                        className="seo-form-textarea"
                        value={formData.metaDescription}
                        maxLength={META_DESCRIPTION_MAX}
                        rows={4}
                        onChange={(e) => handleFieldChange('metaDescription', e.target.value)}
                        disabled={!canEdit}
                      />
                      <div className="seo-char-counter">
                        {formData.metaDescription.length}/{META_DESCRIPTION_MAX} characters
                      </div>
                      <span className="seo-field-hint">Description that appears in search engine results.</span>
                    </div>

                    <div className="seo-form-group">
                      <label>Category Slug</label>
                      <input
                        type="text"
                        className="seo-form-input"
                        value={formData.categorySlug}
                        onChange={(e) => handleFieldChange('categorySlug', e.target.value)}
                        placeholder={getPrimaryProductCategory(productData) || 'category'}
                        disabled={!canEdit}
                      />
                      <span className="seo-field-hint">
                        Optional. When set, used as the category segment in product URLs (case-sensitive).
                        Leave empty to use the first product category.
                      </span>
                    </div>

                    <div className="seo-form-group">
                      <label>URL Slug</label>
                      <div className="seo-slug-input">
                        <span className="seo-slug-prefix">
                          order/{categoryPathPreview}/
                        </span>
                        <input
                          type="text"
                          className="seo-slug-field"
                          value={formData.urlSlug}
                          onChange={(e) => handleFieldChange('urlSlug', e.target.value)}
                          placeholder="product-slug"
                          disabled={!canEdit}
                        />
                        <span className="seo-slug-suffix">/p/id</span>
                      </div>
                      <span className="seo-field-hint">
                        Used in the product URL path: /qatar/order/&#123;categorySlug&#125;/&#123;slug&#125;/p/&#123;id&#125;
                      </span>
                    </div>
                  </div>

                  <div className="seo-basic-right">
                    <div className="seo-form-group">
                      <label>Product Description</label>
                      <textarea
                        className="seo-form-textarea seo-description-textarea"
                        value={formData.productDescription}
                        rows={8}
                        onChange={(e) => handleFieldChange('productDescription', e.target.value)}
                        disabled={!canEdit}
                      />
                      <span className="seo-field-hint">The main description of your product.</span>
                    </div>

                    <div className="seo-form-group">
                      <label>Google Search Preview</label>
                      <div className="seo-google-preview">
                        <div className="seo-preview-title">
                          {formData.metaTitle || 'Product Title'}
                        </div>
                        <div className="seo-preview-url">{previewUrl}</div>
                        <div className="seo-preview-description">
                          {formData.metaDescription ||
                            'Your meta description will appear here. Make it compelling for users to click.'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="seo-advanced-grid">
                  <div className="seo-advanced-card">
                    <h4>Product Images</h4>
                    {formData.images.length === 0 ? (
                      <p className="seo-no-images-msg">No product images available.</p>
                    ) : (
                      <div className="seo-image-alt-list">
                        {formData.images.map((item, index) => (
                          <div key={`${item.image}-${index}`} className="seo-image-alt-item">
                            <div className="seo-image-alt-thumb">
                              <img
                                src={getProductImageUrl(item)}
                                alt={item.altText || productName}
                              />
                            </div>
                            <div className="seo-image-alt-field">
                              <label>Alt Text</label>
                              <input
                                type="text"
                                className="seo-form-input"
                                placeholder="Describe this image for accessibility..."
                                value={item.altText || ''}
                                onChange={(e) => handleAltTextChange(index, e.target.value)}
                                disabled={!canEdit}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="seo-advanced-card">
                    <h4>Product Schema Markup</h4>
                    <textarea
                      className="seo-schema-textarea"
                      value={formData.productSchemaMarkup}
                      onChange={(e) => handleSchemaChange('productSchemaMarkup', e.target.value)}
                      spellCheck={false}
                      disabled={!canEdit}
                    />
                    <span className="seo-field-hint">
                      JSON-LD schema markup for this product. This helps search engines understand your product details.
                    </span>
                  </div>

                  <div className="seo-advanced-card">
                    <h4>Breadcrumbs Schema (JSON-LD)</h4>
                    <textarea
                      className="seo-schema-textarea"
                      value={formData.breadCrumbSchema}
                      onChange={(e) => handleSchemaChange('breadCrumbSchema', e.target.value)}
                      spellCheck={false}
                      disabled={!canEdit}
                    />
                    <span className="seo-field-hint">
                      JSON-LD BreadcrumbList schema for structured navigation data.
                    </span>
                  </div>

                  <div className="seo-advanced-card">
                    <h4>FAQ Schema (JSON-LD)</h4>
                    <textarea
                      className="seo-schema-textarea"
                      value={formData.faqSchema}
                      onChange={(e) => handleSchemaChange('faqSchema', e.target.value)}
                      spellCheck={false}
                      disabled={!canEdit}
                    />
                    <span className="seo-field-hint">
                      JSON-LD FAQPage schema for structured FAQ data.
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="seo-modal-footer">
              <button type="button" className="seo-cancel-button" onClick={onClose} disabled={saving}>
                Cancel
              </button>
              {canEdit && (
                <button
                  type="button"
                  className="seo-save-button seo-save-button-active"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EditSeoModal;
